import React from 'react';
import { BodyText, Button, DataGrid, NavigationItem } from 'ui-toolkit';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { getOffsetName } from '../../utilities/order/getListOfValidTimeZone';
import { formatPhoneNumber } from '../../utilities/shared/formatPhoneNumber';
import { IPresentationDefaultContact } from '../../types/clients/IDefaultContact';
import { clientUSAStateOptions } from '../../types/contacts/IContactUSAStates';
import { IRoleData } from '../../types/roles/IRole';

interface IDefaultContactsTableProps {
  data: IPresentationDefaultContact[];
  loading: boolean;
  onEditContact: (contact: IPresentationDefaultContact) => void;
  roles: IRoleData[];
}

export const DefaultContactsTable: React.FC<IDefaultContactsTableProps> = ({
                                                                             data,
                                                                             loading,
                                                                             onEditContact,
                                                                             roles,
                                                                           }) => {
  const columns: IBootstrapTableColumnProps<IPresentationDefaultContact>[] = React.useMemo(
    () => [
      {
        text: 'Role',
        dataField: 'role',
        sort: true,
        formatter: (_, row) => <>{roles.find((role) => role.name === row.role)?.label}</>,
      },
      {
        text: 'States',
        dataField: 'states',
        sort: true,
        formatter: (_, row) => (
          <>{row.states.map((state) => clientUSAStateOptions.find((s) => s.value === state)!.label).join(', ')}</>
        ),
      },
      {
        text: 'Name',
        dataField: 'admin.fullName',
        sort: true,
      },
      {
        text: 'Email',
        dataField: 'admin.email',
        sort: true,
        formatter: (_, row) => <NavigationItem href={`mailto:${row.admin.email}`} text={row.admin.email} isActive
                                               isInline />,
      },
      {
        text: 'Phone',
        dataField: 'admin.phoneNumber',
        sort: true,
        formatter: (_, row) => <>{formatPhoneNumber(row.admin.phoneNumber || '')}</>,
      },
      {
        text: 'Phone Extension',
        dataField: 'admin.phoneExtension',
        sort: true,
        formatter: (_, row) => <>{row.admin.phoneExtension ? `x${row.admin.phoneExtension}` : '-'}</>,
      },
      {
        text: 'Time Zone',
        dataField: 'admin.timeZone',
        sort: true,
        formatter: (_, row) => <>{row.admin.timeZone ? getOffsetName(row.admin.timeZone) : '-'}</>,
      },
      {
        text: 'Notes',
        dataField: 'notes',
        sort: true,
        formatter: (_, row) => <BodyText style={{ whiteSpace: 'pre-line' }}>{row.notes}</BodyText>,
      },
      {
        text: 'Options',
        dataField: 'id',
        formatter: (_, row) => (
          <Button intent={'ghost'} onClick={() => onEditContact(row)}>
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        ),
      },
    ],
    [roles, onEditContact],
  );

  return <DataGrid columns={columns} keyField={'key'} data={data} loading={loading} />;
};
